import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
  Alert,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import { userManagementService, CustomerV2, CustomerWithContact } from '../../services/userManagementService';
import { normalizeEmail } from '../../utils/normalizeEmail';

interface Step3ReviewCreateProps {
  customersWithContact: CustomerWithContact[];
  selectedCustomers: CustomerV2[];
  onSuccess: (userId: string, userEmail: string) => void;
  onError: (error: string) => void;
}

const Step3ReviewCreate: React.FC<Step3ReviewCreateProps> = ({
  customersWithContact,
  selectedCustomers,
  onSuccess,
  onError,
}) => {
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [emailError, setEmailError] = useState('');
  const [emailChecking, setEmailChecking] = useState(false);

  // Auto-populate email from first customer's selected value
  useEffect(() => {
    if (customersWithContact.length > 0 && customersWithContact[0].selectedValue) {
      const firstEmail = customersWithContact[0].selectedValue;
      setEmail(firstEmail);
      // Auto-generate username from email
      const generatedUsername = userManagementService.generateUsernameFromEmail(firstEmail);
      setUsername(generatedUsername);
    }
  }, [customersWithContact]);

  // Check email availability
  useEffect(() => {
    if (email && email.includes('@')) {
      const checkEmail = async () => {
        setEmailChecking(true);
        setEmailError('');
        try {
          const exists = await userManagementService.checkEmailExists(email);
          if (exists) {
            setEmailError('This email is already registered');
          }
        } catch (error) {
          console.error('Error checking email:', error);
        } finally {
          setEmailChecking(false);
        }
      };

      const timer = setTimeout(checkEmail, 500);
      return () => clearTimeout(timer);
    } else {
      setEmailError('');
    }
  }, [email]);


  const handleCreate = async () => {
    // Validation
    if (!email || !email.includes('@')) {
      onError('Please enter a valid email address');
      return;
    }

    if (!username || username.length < 3) {
      onError('Username must be at least 3 characters');
      return;
    }

    if (emailError) {
      onError('Please fix the email error before proceeding');
      return;
    }

    if (customersWithContact.length === 0) {
      onError('No customers selected');
      return;
    }

    try {
      setLoading(true);
      onError('');

      const normalizedEmail = normalizeEmail(email);

      // Step 1: Create client user (passwordless)
      const userData = await userManagementService.createClientUser({
        email: normalizedEmail,
        username,
        // No password - user will set it via invitation email
        access_level: 'normal', // Default to normal access
      });

      const userId = userData.id;

      // Step 2: Create user_customer_assignment records
      const assignments = customersWithContact.map((customer) => {
        return {
          client_id: userId,
          red_rock_id: customer.customerId,
          red_rock_full_entity_name: customer.customerName,
          is_active: true,
          user_access_type: 'can_view' as const,
          assigned_date: new Date().toISOString().split('T')[0], // YYYY-MM-DD format
          is_current_version: true,
        };
      });

      await userManagementService.createUserAssignments(assignments);

      onSuccess(userId, normalizedEmail);
    } catch (error: any) {
      console.error('Error creating user:', error);
      onError(error.message || 'Failed to create client user');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Step 3 of 3: Review & Create
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Review the information and create the client user account.
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* User Details Form */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
            User Details
          </Typography>

          <TextField
            fullWidth
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={!!emailError}
            helperText={emailError || (emailChecking ? 'Checking availability...' : '')}
            disabled={loading || emailChecking}
            sx={{ mb: 2 }}
            InputProps={{
              endAdornment: emailChecking ? (
                <InputAdornment position="end">
                  <CircularProgress size={20} />
                </InputAdornment>
              ) : null,
            }}
          />

          <TextField
            fullWidth
            label="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            disabled={loading}
            sx={{ mb: 2 }}
            helperText="Username for login (auto-generated from email)"
          />

          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Passwordless Account:</strong> The user account will be created without a password. 
              After creation, you can send an invitation email to the user so they can set their own password.
            </Typography>
          </Alert>
        </Paper>

        {/* Assignments Preview */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
            Customer Assignments ({customersWithContact.length})
          </Typography>
          <List>
            {customersWithContact.map((customer, index) => (
              <ListItem key={customer.customerId}>
                <ListItemText
                  primary={`${index + 1}. ${customer.customerName}`}
                  secondary={`Email: ${customer.selectedValue || 'Not selected'} | Access: can_view`}
                />
              </ListItem>
            ))}
          </List>
        </Paper>

        {/* Create Button */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleCreate}
            disabled={loading || !!emailError || emailChecking}
            sx={{ minWidth: 200 }}
          >
            {loading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Creating...
              </>
            ) : (
              'Create Client User'
            )}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default Step3ReviewCreate;

